import type { CanonicalName } from "../api/parse";

/**
 * Ways a requester can choose to RECEIVE the money once the request is
 * fulfilled. Derived from a pre-quote against the destination corridor.
 */
export enum PayoutMethodType {
  BANK_ACCOUNT = "BANK_ACCOUNT",
  CASH_PICKUP = "CASH_PICKUP",
  PUSH_TO_CARD = "PUSH_TO_CARD",
  MOBILE_WALLET = "MOBILE_WALLET",
}

export interface IPayoutMethod {
  type: PayoutMethodType;
  /** Optional payer / network name (e.g. a specific bank or cash network). */
  payer?: string;
}

/**
 * Recipient reception details, flattened for the wire. Only the fields relevant
 * to the chosen `PayoutMethodType` are populated.
 *
 * `source: 'saved'` + `paymentMethodId` means the instrument is already
 * registered (recipient book / destination-cards picker) — commit reuses the
 * id instead of re-tokenizing. `source: 'new'` (or omitted with raw PAN/CLABE)
 * registers at commit time.
 */
export interface PayoutInput {
  source?: "new" | "saved";
  // BANK_ACCOUNT
  bankId?: string;
  accountNumber?: string;
  // CASH_PICKUP / HOME_DELIVERY
  stateCode?: string;
  payerId?: string;
  // PUSH_TO_CARD
  cardPan?: string;
  cardExpiry?: string;
  cardholderName?: string;
  paymentMethodId?: string;
  brand?: string;
  last4?: string;
  /** CVS dry-run validationId from payout-step pre-validate. */
  cvsValidationId?: string;
  /** Persistent VGS PAN alias — register reuses this instead of raw PAN. */
  numberToken?: string;
  /**
   * Corridor countries the destination card was pre-validated for.
   * Cleared / re-validated when the transfer route countries change.
   */
  validatedFor?: {
    sourceCountry: string;
    destinationCountry: string;
  };
  // MOBILE_WALLET
  carrierId?: string;
  phone?: string;
  // HOME_DELIVERY
  address?: string;
}

/**
 * Recipient identity the sender captures so the operation carries a payee
 * party (OFAC screening). `canonicalName` is normalized via parse/name (or
 * positional fallback); `fullName` drives UI labels.
 */
export interface RecipientInput {
  fullName: string;
  canonicalName: CanonicalName | null;
  phone?: string;
  /**
   * Recipient-book key when the user picked a recent entry. Used to highlight
   * exactly one row in the recent list (fullName alone can collide).
   */
  recipientKey?: string;
  /**
   * Legacy fields from drafts persisted before fullName/canonicalName.
   * Prefer `canonicalName` / `fullName` when present.
   */
  firstName?: string;
  lastName?: string;
}
